// Formato de factura · logo y datos del emisor (pedido de Rodrigo 2026-10-21: «falta el logo, falta
// que la información esté correcta: el giro, la dirección, todo el encabezado»). El logo va arriba a
// la izquierda de la factura impresa (no entra al XML); giro, dirección, comuna y ciudad son los datos
// del emisor que se imprimen y viajan en el XML.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, pedirArchivo } from '../api/cliente';
// El logo de la factura de Random (FCV 3172), sin «COMERCIAL»: la marca es MADECI a secas.
import logoMadeci from '../assets/logo-madeci-factura.png';
import { PanelError } from '../comun/Estados';

export interface DatosEmisor {
  giro: string;
  direccion: string;
  comuna: string;
  ciudad: string | null;
}

const aBase64 = async (b: Blob) => {
  const bytes = new Uint8Array(await b.arrayBuffer());
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000)
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
};

export function LogoEmisor({
  empresaId,
  editable,
  tieneLogo,
  emisor,
  alCambiar,
}: {
  empresaId: string;
  editable: boolean;
  tieneLogo: boolean;
  emisor: DatosEmisor | null;
  alCambiar: () => void;
}) {
  const qc = useQueryClient();
  const clave = ['dte', 'formato-impresion', empresaId];
  const logo = useQuery({
    queryKey: [...clave, 'logo', tieneLogo],
    queryFn: async () => {
      const b = await pedirArchivo('GET', '/dte/formato-impresion/logo', { query: { empresaId } });
      return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(b) : null;
    },
    enabled: tieneLogo,
    retry: false,
  });
  const subir = useMutation({
    mutationFn: async (png: Blob | null) =>
      api.put('/dte/formato-impresion/logo', {
        empresaId,
        pngBase64: png ? await aBase64(png) : null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: clave });
      alCambiar();
    },
  });
  const [e, setE] = useState<DatosEmisor>({ giro: '', direccion: '', comuna: '', ciudad: '' });
  useEffect(() => {
    if (emisor) setE({ ...emisor, ciudad: emisor.ciudad ?? '' });
  }, [emisor]);
  const guardarEmisor = useMutation({
    mutationFn: () =>
      api.put('/dte/emisor/datos', {
        empresaId,
        giro: e.giro.trim(),
        direccion: e.direccion.trim(),
        comuna: e.comuna.trim(),
        ciudad: e.ciudad?.trim() || null,
        motivo: 'Datos del emisor en la factura impresa',
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: clave });
      alCambiar();
    },
  });
  const cambioEmisor =
    !!emisor &&
    (e.giro !== emisor.giro ||
      e.direccion !== emisor.direccion ||
      e.comuna !== emisor.comuna ||
      (e.ciudad ?? '') !== (emisor.ciudad ?? ''));
  const set = (k: keyof DatosEmisor) => (ev: React.ChangeEvent<HTMLInputElement>) =>
    setE({ ...e, [k]: ev.target.value });

  return (
    <>
      <fieldset>
        <legend>Logo (arriba a la izquierda)</legend>
        <div className="formato-logo">
          <div className="formato-logo-vista">
            {tieneLogo && logo.data ? (
              <img src={logo.data} alt="Logo de la factura" />
            ) : (
              <span className="apagado">Sin logo</span>
            )}
          </div>
          {editable && (
            <div className="formato-logo-acciones">
              <label className="boton-archivo">
                Subir logo (PNG)
                <input
                  type="file"
                  accept="image/png"
                  aria-label="Subir logo"
                  onChange={(ev) => {
                    const f = ev.target.files?.[0];
                    if (f) subir.mutate(f);
                    ev.target.value = '';
                  }}
                />
              </label>
              <button
                type="button"
                disabled={subir.isPending}
                onClick={async () => subir.mutate(await (await fetch(logoMadeci)).blob())}
              >
                Usar el logo de MADECI
              </button>
              {tieneLogo && (
                <button
                  type="button"
                  className="peligro"
                  disabled={subir.isPending}
                  onClick={() => subir.mutate(null)}
                >
                  Quitar
                </button>
              )}
            </div>
          )}
        </div>
        <p className="ayuda">PNG de hasta 1200 × 600 píxeles y 400 KB. Sale a color.</p>
        {subir.isError && <PanelError error={subir.error} />}
      </fieldset>
      <fieldset>
        <legend>Datos de la empresa (bajo el nombre)</legend>
        {!emisor ? (
          <p className="apagado">La empresa todavía no tiene emisor de documentos configurado.</p>
        ) : (
          <>
            <div className="formato-fila">
              <label className="campo campo-ancho">
                <span className="etiqueta">Giro</span>
                <input
                  aria-label="Giro del emisor"
                  disabled={!editable}
                  maxLength={80}
                  value={e.giro}
                  onChange={set('giro')}
                />
              </label>
            </div>
            <div className="formato-fila">
              <label className="campo campo-ancho">
                <span className="etiqueta">Dirección</span>
                <input
                  aria-label="Dirección del emisor"
                  disabled={!editable}
                  maxLength={70}
                  value={e.direccion}
                  onChange={set('direccion')}
                />
              </label>
              <label className="campo">
                <span className="etiqueta">Comuna</span>
                <input
                  aria-label="Comuna del emisor"
                  disabled={!editable}
                  maxLength={20}
                  value={e.comuna}
                  onChange={set('comuna')}
                />
              </label>
              <label className="campo">
                <span className="etiqueta">Ciudad</span>
                <input
                  aria-label="Ciudad del emisor"
                  disabled={!editable}
                  maxLength={20}
                  value={e.ciudad ?? ''}
                  onChange={set('ciudad')}
                />
              </label>
            </div>
            <p className="ayuda">
              Estos datos también van en el XML de cada documento: deben ser los que la empresa
              tiene registrados en el SII.
            </p>
            {editable && (
              <button
                type="button"
                className="primario"
                disabled={!cambioEmisor || guardarEmisor.isPending}
                onClick={() => guardarEmisor.mutate()}
              >
                {guardarEmisor.isPending ? 'Guardando…' : 'Guardar datos de la empresa'}
              </button>
            )}
            {guardarEmisor.isError && <PanelError error={guardarEmisor.error} />}
          </>
        )}
      </fieldset>
    </>
  );
}
